import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { listPartners, deletePartner, updatePartner } from "../../../api/cms";
import SortableTableRow from "../../../components/SortableTableRow";

function byOrder(a, b) {
  if (a.order == null) return 1;
  if (b.order == null) return -1;
  return a.order - b.order;
}

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = () => {
    setLoading(true);
    listPartners()
      .then(data => setPartners([...data].sort(byOrder)))
      .catch(() => setError("Failed to load partners"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    await deletePartner(id);
    load();
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = partners.findIndex(p => p.id === active.id);
    const newIndex = partners.findIndex(p => p.id === over.id);
    const reordered = arrayMove(partners, oldIndex, newIndex);
    setPartners(reordered);
    setSaving(true);
    await Promise.all(reordered.map((p, i) => updatePartner(p.id, { ...p, order: i })));
    setSaving(false);
  };

  if (loading) return <p>Loading…</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Partners</h1>
          {saving && <span className="text-sm text-gray-400">Saving…</span>}
        </div>
        <Link to="/admin/partners/new" className="bg-bahyellow rounded px-4 py-2 text-sm font-semibold hover:opacity-90">
          + Add partner
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-3 w-8" />
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Link</th>
              <th className="px-4 py-3 font-semibold">Logo</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={partners.map(p => p.id)} strategy={verticalListSortingStrategy}>
              <tbody className="divide-y divide-gray-100">
                {partners.map((p) => (
                  <SortableTableRow key={p.id} id={p.id}>
                    <td className="px-4 py-3">{p.name}</td>
                    <td className="px-4 py-3 text-blue-600 truncate max-w-xs"><a href={p.link} target="_blank" rel="noopener noreferrer">{p.link}</a></td>
                    <td className="px-4 py-3"><img src={p.logo} alt={p.name} className="h-8 object-contain" /></td>
                    <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                      <Link to={`/admin/partners/${p.id}/edit`} className="text-blue-600 hover:underline">Edit</Link>
                      <button onClick={() => handleDelete(p.id)} className="text-red-500 hover:underline">Delete</button>
                    </td>
                  </SortableTableRow>
                ))}
                {partners.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No partners yet</td></tr>
                )}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>
    </div>
  );
}
